import {
  alpha,
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { PushPin, PushPinOutlined } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

import { ProducerNavModule } from './producer-nav-config';

interface SecNavPanelProps {
  module: ProducerNavModule | undefined;
  isVisible: boolean;
  isHovered: boolean;
  isPinned: boolean;
  onTogglePin: () => void;
  onHover: (moduleId: string | null) => void;
}

export function SecNavPanel({
  module,
  isVisible,
  isHovered,
  isPinned,
  onTogglePin,
  onHover,
}: SecNavPanelProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  if (!module) return null;

  const handleNavClick = (href: string) => {
    navigate(href);
  };

  return (
    <Box
      sx={{
        width: isVisible ? 175 : 0,
        overflow: 'hidden',
        bgcolor: 'background.default',
        boxShadow: '4px 0 8px rgba(0, 0, 0, 0.15)',
        borderRadius: 3,
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        flexShrink: 0,
        transition: 'width 0.2s ease',
        zIndex: isPinned ? 'auto' : 1200,
        ...(isPinned
          ? {}
          : {
              position: 'absolute',
              top: 0,
              left: 72,
              height: '100%',
              boxShadow: '4px 0 8px rgba(0, 0, 0, 0.15)',
            }),
        '@media print': { display: 'none' },
      }}
      onMouseEnter={() => onHover(module.id)}
      onMouseLeave={() => onHover(null)}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pl: 3,
          pt: 2,
          pb: 3,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
          }}
        >
          {module.label}
        </Typography>
        <IconButton
          size="small"
          onClick={onTogglePin}
          sx={{
            color: 'text.primary',
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.15s ease',
            pointerEvents: isHovered ? 'auto' : 'none',
            pr: 2,
          }}
        >
          {isPinned ? (
            <PushPin fontSize="small" />
          ) : (
            <PushPinOutlined fontSize="small" />
          )}
        </IconButton>
      </Box>

      {/* Nav sections */}
      {(
        module.sections ??
        (module.href
          ? [{ items: [{ label: module.label, href: module.href }] }]
          : [])
      ).map((section, sIdx) => (
        <Box key={sIdx} sx={{ px: 2, mt: sIdx > 0 ? 1 : 0 }}>
          {section.title && (
            <Typography
              variant="body1"
              sx={{
                color: 'text.disabled',
                px: 1,
                mb: 2,
                display: 'block',
              }}
            >
              {section.title}
            </Typography>
          )}
          <List dense disablePadding>
            {section.items.map((item) => (
              <ListItemButton
                key={item.href}
                selected={pathname === item.href}
                onClick={() => handleNavClick(item.href)}
                sx={{
                  borderRadius: '6px',
                  mb: 0.5,
                  '&.Mui-selected, &.Mui-selected:hover': {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.15),
                  },
                  '&:hover': {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  slotProps={{
                    primary: {
                      variant: 'body1',
                      color:
                        pathname === item.href
                          ? 'text.primary'
                          : 'text.secondary',
                    },
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      ))}
    </Box>
  );
}
